import React, { useCallback, useEffect, useReducer, useRef } from 'react'
import { Button, colors, Divider, Row, useInfoSheet, H2, LayoutBand, Separator, Skeleton } from '@fs/zion-ui'
import { ArrowCaret, ControlSettingsAdjust } from '@fs/zion-icon'
import { useParams, useLocation } from '@fs/zion-router'
import { Title } from '@fs/zion-head'
import { useTranslation } from 'react-i18next'
import axios, { useZionAxios } from '@fs/zion-axios'
import { usePageView } from '@fs/zion-analytics'
import { type TreeGroupDetails } from '@fs/zion-tree-types'
import { type Contributions, type Contribution } from '@fs/zion-tree-contributions'
import { useFeatureFlag } from '@fs/zion-flags'
import useGroup from '../services/useGroup'
import HiddenTreeMessage from '../components/details/researcher-trees/HiddenTreeMessage'
import useTree from '../services/useTree'
import Table, { TableSkeleton } from '../components/details/researcher-trees/contributions/Table'
import OptionsSideSheet from '../components/details/researcher-trees/contributions/OptionsSideSheet'
import { type TREE_CHANGES_PATH } from '../routes'
import { type PathParamMap } from '../types/pathParamTypes'
import SkeletonAnimationSyncDecorator from '../components/SkeletonAnimationSyncDecorator'

type Contributor = {
  cisId: string
  contactName?: string
}

type QueryParamsObj = {
  cisId?: string
  from?: string
  oldestToNewest?: boolean
  timestamp?: number
  useHistoryPersons?: boolean
}

type ContributionState = {
  contributionList?: Contribution[]
  error?: unknown
  isLoadingNextPage?: boolean
  nextPageToken?: Contributions['nextPageToken']
  persons?: Contributions['persons']
  queryParamsObj: QueryParamsObj
}

type ContributionAction =
  | {
      type: 'INIT'
      contributionList: Contribution[]
      nextPageToken: Contributions['nextPageToken']
      persons: Contributions['persons']
    }
  | { type: 'CHANGE_OPTIONS'; queryParamsObj: Partial<QueryParamsObj> }
  | { type: 'START_UPDATE' }
  | {
      type: 'COMPLETE_UPDATE'
      contributionList: Contribution[]
      nextPageToken: Contributions['nextPageToken']
      persons: Contributions['persons']
    }
  | { type: 'ERROR'; error: unknown }

const contributionReducer = (state: ContributionState, action: ContributionAction): ContributionState => {
  switch (action.type) {
    case 'INIT': {
      return {
        ...state,
        contributionList: action.contributionList,
        persons: action.persons,
        nextPageToken: action.nextPageToken,
      }
    }
    case 'CHANGE_OPTIONS': {
      const newQueryParamsObj = Object.fromEntries(
        Object.entries({ ...state.queryParamsObj, ...action.queryParamsObj }).filter(([name, value]) => {
          if ((name === 'timestamp' && value === 0) || value === undefined) {
            return false
          }
          return true
        })
      ) as QueryParamsObj
      return { queryParamsObj: newQueryParamsObj }
    }
    case 'START_UPDATE': {
      return { ...state, isLoadingNextPage: true }
    }
    case 'COMPLETE_UPDATE': {
      return {
        ...state,
        contributionList: [...(state.contributionList ?? []), ...action.contributionList],
        persons: { ...state.persons, ...action.persons },
        nextPageToken: action.nextPageToken,
        isLoadingNextPage: false,
      }
    }
    case 'ERROR': {
      return { ...state, error: action.error, isLoadingNextPage: false }
    }
    default: {
      throw new Error('Wrong type used for contributionReducer')
    }
  }
}

const getContributions = (treeId: string, queryParamsObj: QueryParamsObj): Promise<{ data: Contributions }> => {
  const params = { ...queryParamsObj }
  // the backend says we can't send timestamp and from together - TF-3601
  if (queryParamsObj.from) {
    delete params.timestamp
  }
  // If timeStamp is valid then oldestToNewest needs to be true
  if (params.timestamp) {
    params.oldestToNewest = true
  }
  // URLSearchParams coerces all values to strings at runtime; cast required to satisfy its type signature
  const queryParams = new URLSearchParams(params as unknown as Record<string, string>)
  return axios.get(
    `/service/tree/tree-data/user/all/tree/${treeId}/contributions${queryParams.size ? `?${queryParams}` : ''}`
  )
}

const initialState = {
  queryParamsObj: {
    oldestToNewest: false,
  },
}

const transformResponse = (res: string): Contributor[] => {
  const parsed = JSON.parse(res) as { contributors: Contributor[] }
  return parsed.contributors
}

export default function TreeChangesPage(): JSX.Element {
  usePageView({ page_detail: 'ReT: Tree Changes' })
  const [t] = useTranslation()
  const { groupId } = useParams() as PathParamMap<typeof TREE_CHANGES_PATH>
  const { state } = useLocation() as { state: { group?: TreeGroupDetails } }
  const optionsInfoSheet = useInfoSheet()
  const newSettingsPageOn = useFeatureFlag('treeWeb_groupManagement_newSettingsPage').isOn
  const [contributionsState, contributionsDispatch] = useReducer(contributionReducer, initialState)
  const isCurrent = useRef<boolean>()
  const group = state?.group
  const { data: groupDetails } = useGroup(group?.id ? undefined : groupId)
  const selectedGroup = group ?? groupDetails
  const treeId = selectedGroup?.treeIds?.[0]
  const { data: treeDetails, loading: treeLoading } = useTree(treeId)
  const loading = !selectedGroup || treeLoading
  const isHidden = treeDetails?.hidden

  // This is a deliberate choice to not use the historical persons for the tree changelog.
  const historicalPersonsIsOn = false

  const [{ data: contributors }] = useZionAxios<Contributor[]>({
    url: treeId && !isHidden ? `/service/tree/tree-data/user/all/tree/${treeId}/contributors` : undefined,
    transformResponse,
  })

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  })

  const queryParamsObj = contributionsState.queryParamsObj
  useEffect(() => {
    if (treeId && !isHidden) {
      getContributions(treeId, { ...queryParamsObj, useHistoryPersons: historicalPersonsIsOn })
        .then(({ data }) => {
          if (isCurrent.current) {
            contributionsDispatch({
              type: 'INIT',
              contributionList: data.contributions,
              persons: data.persons,
              nextPageToken: data.nextPageToken,
            })
          }
          return undefined
        })
        .catch((err) => {
          if (isCurrent.current) {
            console.error(err)
            contributionsDispatch({ type: 'ERROR', error: err })
          }
        })
    }
  }, [isHidden, queryParamsObj, treeId, historicalPersonsIsOn])

  const loadNextPage = useCallback(() => {
    if (!treeId) return
    if (!contributionsState.isLoadingNextPage && contributionsState?.nextPageToken) {
      contributionsDispatch({ type: 'START_UPDATE' })
      getContributions(treeId, {
        ...queryParamsObj,
        from: contributionsState.nextPageToken,
        useHistoryPersons: historicalPersonsIsOn,
      })
        .then(({ data }) => {
          if (isCurrent.current) {
            contributionsDispatch({
              type: 'COMPLETE_UPDATE',
              contributionList: data.contributions,
              persons: data.persons,
              nextPageToken: data.nextPageToken,
            })
          }
          return undefined
        })
        .catch((err) => {
          if (isCurrent.current) {
            console.error(err)
            contributionsDispatch({
              type: 'ERROR',
              error: err,
            })
          }
        })
    }
  }, [
    contributionsState.isLoadingNextPage,
    contributionsState.nextPageToken,
    queryParamsObj,
    treeId,
    historicalPersonsIsOn,
  ])

  if (loading) {
    return <TreeChangesPageSkeleton />
  }

  if (isHidden) {
    return (
      <>
        <Separator />
        <HiddenTreeMessage />
      </>
    )
  }

  const changeOptions = (paramsObj: QueryParamsObj): void => {
    contributionsDispatch({ type: 'CHANGE_OPTIONS', queryParamsObj: paramsObj })
  }
  const handleSort = (columnId: string, asc: boolean): void => {
    if (columnId === 'column3') {
      setTimeout(() => {
        contributionsDispatch({ type: 'CHANGE_OPTIONS', queryParamsObj: { oldestToNewest: asc } })
      }, 50)
    }
  }

  return (
    <>
      <Title>{t('group-management.latest-changes-page.title', { groupName: selectedGroup.name })}</Title>
      <LayoutBand maxWidth="xxl" color={colors.gray00} marginY="nano">
        <Button
          data-testid="back-to-group-button"
          Icon={ArrowCaret}
          iconDirection="backward"
          to={
            newSettingsPageOn
              ? `/research-tree/${selectedGroup.id}/latest-changes`
              : `/research-tree/${selectedGroup.id}/details`
          }
        >
          {t('group-management.back-to-group.action', { groupName: selectedGroup.name })}
        </Button>
      </LayoutBand>

      <Divider fullWidth />

      <LayoutBand maxWidth="xxl" color={colors.gray00} marginY="nano">
        <Row>
          <H2 growX size="H5">
            {t('group-management.latest-changes.heading')}
          </H2>
          <Button
            data-testid="tree-changes-options-button"
            onClick={optionsInfoSheet.handleClick}
            emphasis="medium"
            Icon={ControlSettingsAdjust}
            linkName="tree changes options button"
          >
            {t('group-management.options.action')}
          </Button>
        </Row>

        <Separator size="nano" />

        <Table
          contributionList={contributionsState.contributionList}
          loading={!contributionsState.contributionList && !contributionsState.error}
          error={contributionsState.error}
          contributionsPersons={contributionsState.persons}
          isLastPage={!contributionsState.nextPageToken}
          loadNextPage={loadNextPage}
          oldestToNewest={contributionsState?.queryParamsObj?.oldestToNewest}
          onSort={handleSort}
          removeSort={contributionsState.queryParamsObj.timestamp}
        />
      </LayoutBand>
      <OptionsSideSheet infoSheetConfig={optionsInfoSheet} contributors={contributors} changeOptions={changeOptions} />
    </>
  )
}

export function TreeChangesPageSkeleton(): JSX.Element {
  return (
    <SkeletonAnimationSyncDecorator>
      <LayoutBand maxWidth="xxl" color={colors.gray00} marginY="nano">
        {/* eslint-disable-next-line test-selectors/button -- NA */}
        <Skeleton.Button />
      </LayoutBand>
      <Divider fullWidth />
      <LayoutBand maxWidth="xxl" color={colors.gray00} marginY="nano">
        <Row>
          <Skeleton.H5 growX />
          <div growX />
          {/* eslint-disable-next-line test-selectors/button -- NA */}
          <Skeleton.Button minWidth={120} />
        </Row>
        <Separator size="nano" />
        <TableSkeleton />
      </LayoutBand>
    </SkeletonAnimationSyncDecorator>
  )
}
